import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState, type FormEvent } from "react";
import { z } from "zod";
import { useAuth } from "@/lib/auth";
import { readCredentials } from "@/lib/login-form";
import { MFA_ENROLLED_NOTICE } from "@/lib/mfa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const searchSchema = z.object({
  next: z.string().optional(),
  notice: z.literal(MFA_ENROLLED_NOTICE).optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: searchSchema,
  component: LoginPage,
});

function LoginPage() {
  const { signIn, sendReset } = useAuth();
  const navigate = useNavigate();
  const { next, notice } = Route.useSearch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // The DOM is authoritative for credentials — see lib/login-form.ts for why.
  const readForm = (form: HTMLFormElement) =>
    readCredentials(new FormData(form), { email, password });

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const creds = readForm(e.currentTarget);
    setBusy(true);
    setError(null);
    setResetSent(false);
    const res = await signIn(creds.email, creds.password);
    setBusy(false);

    if ("ok" in res && res.ok) {
      navigate({ to: next ?? "/dashboard" });
      return;
    }
    if ("mfaResolver" in res) {
      // Stash the resolver on window for the verify screen (transient — same session).
      // A full impl would use a context, but this is sufficient for MVP.
      (window as unknown as { __mfaResolver?: unknown }).__mfaResolver = res.mfaResolver;
      navigate({ to: "/mfa-verify" });
      return;
    }
    setError("error" in res ? res.error : "Sign-in failed");
  }

  async function onForgotPassword() {
    const addr = (formRef.current ? readForm(formRef.current).email : email).trim();
    if (!addr) {
      setError("Enter your email address first, then choose Forgot password.");
      return;
    }
    setBusy(true);
    setError(null);
    setResetSent(false);
    const res = await sendReset(addr);
    setBusy(false);
    if (res.ok) setResetSent(true);
    else setError(res.error);
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6 rounded-xl border bg-card p-8 shadow-sm">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">PinStaQ Admin</h1>
          <p className="text-sm text-muted-foreground">Sign in with an admin account.</p>
        </div>
        {notice === MFA_ENROLLED_NOTICE && (
          <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            Two-factor authentication is set up. Sign in again with your authenticator code to continue.
          </p>
        )}
        <form ref={formRef} className="space-y-4" onSubmit={onSubmit}>
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium">
              Password
            </label>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          {resetSent ? (
            <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              If that address has an account, a reset link is on its way. Check spam too.
            </p>
          ) : null}
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <button
            type="button"
            onClick={onForgotPassword}
            disabled={busy}
            className="w-full text-center text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-50"
          >
            Forgot password?
          </button>
        </form>
      </div>
    </div>
  );
}
